import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { LocationSectorApi } from '../../../../../../../../core/api/location-sectors/location-sectors.api';
import { SearchLocationSectorParams } from '../../../../../../../../core/api/location-sectors/location-sectors.type';
import { ArchiveSectorDialogService } from '../../../../../../../../core/components/archive-sector-dialog/archive-sector-dialog.service';
import { ActionButton, ActionTypeEnum, TreeViewItem } from '../../../../../../../../core/components/fc-tree-view/fc-tree.types';
import { Location } from '../../../../../../../../core/interfaces';
import { LocationSector } from '../../../../../../../../core/interfaces/locations-sectors';
import { fetchAllInBatches } from '../../../../../../../../core/utils/fetch-all-in-batches';
import { LinkEquipmentDialogService } from '../../../../../dialog-link-equipment/dialog-link-equipment.service';
import { DialogLocationSectorComponent, DialogLocationSectorData, DialogLocationSectorResult } from '../../../../../dialog-location-sector/dialog-location-sector.component';
import { EquipmentLinkService } from '../equipment/equipment.service';
import { LocationLinkEnvironmentService } from '../location-environment/location-environment.service';

@Injectable({
  providedIn: 'root'
})
export class LocationLinkSectorService {
  public constructor(
    private readonly locationSectorApi: LocationSectorApi,
    private readonly matDialog: MatDialog,
    private readonly translateService: TranslateService,
    private readonly locationLinkEnvironmentService: LocationLinkEnvironmentService,
    private readonly equipmentLinkService: EquipmentLinkService,
    private readonly archiveSectorDialogService: ArchiveSectorDialogService,
    private readonly linkEquipmentDialogService: LinkEquipmentDialogService
  ) {}

  public async searchLocationSectors(params?: SearchLocationSectorParams): Promise<LocationSector[]> {
    return fetchAllInBatches(this.locationSectorApi, params);
  }

  public async searchLocationSectorsWithEnvironmentsAndEquipments(params?: SearchLocationSectorParams): Promise<LocationSector[]> {
    return fetchAllInBatches({ search: this.locationSectorApi.searchWithEnvironmentsAndEquipments.bind(this.locationSectorApi) }, params);
  }

  public formatSectors(sectors: LocationSector[]): TreeViewItem[] {
    return sectors.map((sector) => {
      const sectorItem: TreeViewItem =  {
        id: sector.id,
        text: sector.name,
        description: sector.description,
        searchTerm: [sector.name, sector.description].filter(Boolean).join(' ').toLowerCase(),
        archived: sector.archived,
        archivedText: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVED'),
        icon: 'sector',
        metadata: 'sector',
        action: {
          type: ActionTypeEnum.menu,
          buttons: this.sectorsButtons(sector)
        }
      };

      const environments = this.locationLinkEnvironmentService.formatEnvironments(sector.locationEnvironments, sectorItem);
      const equipments = this.equipmentLinkService.formatEquipments(sector.equipments, sectorItem);

      sectorItem.children = [...environments, ...equipments];

      return sectorItem;
    });
  }

  private sectorsButtons(locationSector: LocationSector): ActionButton[] {
    const color = '#676767';

    return [
      {
        text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.EDIT_SECTOR'),
        icon: 'edit',
        color,
        action: async () => {
          await this.openEditLocationSectorDialog({
            locationSectorId: locationSector.id
          });
        }
      },
      {
        text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.CREATE_ENVIRONMENT'),
        icon: 'environment_outline',
        color,
        action: async () => {
          await this.locationLinkEnvironmentService.openCreateLocationEnvironmentDialog({
            location: locationSector.location,
            locationSectorId: locationSector.id
          });
        }
      },
      {
        text: this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.LINK_EQUIPMENT'),
        icon: 'equipment',
        color,
        action: async () => {
          await this.openLinkEquipmentDialog({
            locationSector
          });
        }
      },
      {
        text: locationSector.archived
          ? this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.UNARCHIVE_SECTOR_TITLE')
          : this.translateService.instant('LOCATIONS.DIALOG.TABS.TAB_LINKS.ARCHIVE_SECTOR_TITLE'),
        icon: locationSector.archived ? 'unarchive_outline' : 'archive_outline',
        color,
        action: async () => {
          await this.openArchiveOrUnarchiveLocationSectorDialog({
            locationSector
          });
        }
      },
    ];
  }

  public async openCreateLocationSectorDialog({ location }: { location: Location }): Promise<void> {
    await this.openLocationSectorDialog({ location });
  }

  private async openEditLocationSectorDialog({ locationSectorId }: { locationSectorId: string }): Promise<void> {
    await this.openLocationSectorDialog({ locationSectorId });
  }

  public async openLocationSectorDialog({ locationSectorId, location }: { locationSectorId?: string; location?: Location; }): Promise<DialogLocationSectorResult> {
    const locationSector = locationSectorId ? await this.locationSectorApi.get(locationSectorId) : {
      id: null,
      name: '',
      description: '',
      location: location
    };

    const result = await this.matDialog
      .open<DialogLocationSectorComponent, DialogLocationSectorData, DialogLocationSectorResult>(DialogLocationSectorComponent, {
        panelClass: 'dialog-location-sector',
        data: {
          locationSector
        }
      })
      .afterClosed()
      .toPromise();

    if (result && result.reload) {
      window.emitEvent('location-links::reload-tree-view');
      return result;
    }
  }

  private async openArchiveOrUnarchiveLocationSectorDialog({ locationSector }: { locationSector: LocationSector }): Promise<void> {

    const result = await this.archiveSectorDialogService.open({
      sectorId: locationSector.id,
      archived: locationSector.archived,
      name: locationSector.name
    });

    if (!result || !result.reload) {
      return;
    }

    window.emitEvent('location-links::reload-tree-view');
  }

  private async openLinkEquipmentDialog({ locationSector }: { locationSector: LocationSector }): Promise<void> {

    const result = await this.linkEquipmentDialogService.open({
      locationId: locationSector.location.id,
      locationSectorId: locationSector.id,
      locationEnvironmentId: null,
    });

    if (!result || !result.reload) {
      return;
    }

    window.emitEvent('location-links::reload-tree-view');

  }
}
